import type { IOverleveringChecklistReport } from "@mcapps/shared/startupChecklists/templates/901-overlevering/901Schema";
import type { IForenkletStyringNivaa1Report } from "@mcapps/shared/startupChecklists/templates/902-forenklet-styring/902InitialData";
import { StartupChecklistHeader } from "../../startupChecklists/StartupChecklistHeader";
import { isStartupChecklistTemplate } from "../../startupChecklists/startupChecklistUtils";
import { useDirectReportSave } from "../useDirectReportSave";
import { ActionIcon, Alert, Button, Divider, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IDocxImage } from "@mctypes/global.types";
import { IconAlertTriangle, IconBraces, IconDeviceFloppy, IconFileTypeDocx } from "@tabler/icons-react";
import * as R from "ramda";
import React, { useEffect, useState } from "react";
import { Prompt, useLocation } from "react-router-dom";
import { MCLoader } from "../../../../components";
import { ZodErrorModal } from "../../../../components/ZodErrorModal/ZodErrorModal";
import { setShowFailedMessageThunk } from "../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { IBygningsfysikkPremissRapport } from "@mcapps/shared/reportSchemas/bygningsfysikk/bygningsfysikkPremissrapport/bygningsfysikkPremissrapportSchema";
import { IElektroSamsvarserklaringEkomnettRapport } from "@mcapps/shared/reportSchemas/elektroSamsvarserklaringEkomnettSchema";
import { IElektroSamsvarserklaringFELRapport } from "@mcapps/shared/reportSchemas/elektroSamsvarserklaringFELSchema";
import { IGeoTeknikkRapport } from "@mcapps/shared/reportSchemas/geoteknikSchema";
import { IGKRapport } from "@mcapps/shared/reportSchemas/gkReportSchema";
import { IInternJuridiskUnderkonsulentRapport } from "@mcapps/shared/reportSchemas/internJuridiskSchema";
import { ISJArapport } from "@mcapps/shared/reportSchemas/sjaSchema";
import { MCApplicationsUrlEnum } from "@mcapps/shared/sharedConstants";
import { useGetFilesFromAzureBlobStorageQuery } from "../../../azureBlobStorageRTK";
import { useNetworkStatus } from "../../../utils";
import { doLogUsage } from "../../admin/restQueries";
import AkustikkPremissrapport from "../akustikk/AkustikkPremissrapport";
import Brannrapport from "../brannrapport/Brannrapport";
import { IBrandrapportConfig } from "../brannrapport/types";
import BygningsfysikkPremissrapport from "../bygningsfysikk/BygningsfysikkPremissrapport";
import { EksempelRapport } from "../eksempelrapport/EksempelRapport";
import RapportSamsvarserklaringEkomnett from "../elektro/samsvarserklaring/RapportSamsvarserklaringEkomnett";
import RapportSamsvarserklaringFEL from "../elektro/samsvarserklaring/RapportSamsvarserklaringFEL";
import SJARapport from "../elektro/SJA/SJArapport";
import GeoteknikOmraadestabilitetsRapport from "../geoteknik/GeoteknikOmraadestabilitetsRapport";
import GeoteknikRapport from "../geoteknik/Geoteknikrapport";
import GKrapport from "../gkRapport/GKrapport";
import UnderkonsulentRapport from "../internjuridisk/UnderkonsulentRapport";
import { doGenerateReportAppDocx } from "../reportBoardAPI";
import { ReportDocxDataModal } from "../ReportDocxDataModal";
import {
  buildCurrentReportDocxData,
  selectCurrentReportValidation,
  selectEditedDataForCurrentReport,
  selectHttpSingleReportBoard,
  selectIsCurrentReportValid,
  selectIsEditedDataEqualToReportData,
  selectReportData,
} from "../reportSelectors";
import { setEditedDataInRedux, updateReportDataThunk } from "../reportSlice";
import OverleveringChecklistReport from "../../startupChecklists/templates/901-overlevering/OverleveringChecklistReport";

const SingleReport: React.FC = () => {
  const [reportErrorsModalOpen, setReportErrorsModalOpen] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { isOnline } = useNetworkStatus();
  const [openJsonModal, setOpenJsonModal] = React.useState(false);
  const project = useAppSelector((state) => state.adminReducer.project);
  const editedDataCurrentReport = useAppSelector(selectEditedDataForCurrentReport);

  const user = useAppSelector((state) => state.globalReducer.user);
  const activeReport = useAppSelector(selectHttpSingleReportBoard);
  const reportData = useAppSelector(selectReportData);
  const isEditedDataEqual = useAppSelector(selectIsEditedDataEqualToReportData);
  const reportErrors = useAppSelector(selectCurrentReportValidation);
  const reportIsValid = useAppSelector(selectIsCurrentReportValid);
  const reportId = useAppSelector((state) => state.reportReducer.currentReportId);

  const reportTemplateId = activeReport?.template;
  const direct = isStartupChecklistTemplate(reportTemplateId);
  const startupChecklist = isStartupChecklistTemplate(reportTemplateId);
  const directSave = useDirectReportSave({
    enabled: direct,
    projectId: project._id,
    reportId: activeReport?._id,
    revision: activeReport?.revision ?? 0,
    online: isOnline,
  });
  const hasUnsavedChanges = isEditedDataEqual === false || (direct && (directSave.saving || !!directSave.error));
  const path = `${project._id}/report/${reportId}/images`;
  const { data: files = [] } = useGetFilesFromAzureBlobStorageQuery(
    { projectId: project._id, path },
    { skip: !reportId },
  );

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const location = useLocation();
  const onLocationChange = (nextLocation: { pathname: string }) => {
    // Query and hash changes keep the current report mounted.
    if (nextLocation.pathname === location.pathname) return true;
    return "Du har ulagrede endringer. Er du sikker på at du vil forlate siden?";
  };

  const init = () => {
    if (!activeReport?.data) return;
    doLogUsage(project, MCApplicationsUrlEnum.REPORT, activeReport?.template.toString());
  };

  const dispatch = useAppDispatch();

  const onSave = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      await dispatch(updateReportDataThunk(activeReport._id, activeReport.template));
    } catch (cause) {
      setSaveError(cause?.data?.message ?? cause?.message ?? "Kunne ikke lagre rapporten. Prøv igjen.");
    } finally {
      setIsSaving(false);
    }
  };
  const setData = (_editedData: object) => {
    const data = R.clone(_editedData);
    dispatch(
      setEditedDataInRedux({
        code: activeReport.template,
        data: data as any,
      }),
    );
    //setReportParseErrors(null);
  };

  const generateDocx = async () => {
    const images: IDocxImage[] = files.map((file) => {
      return {
        tag: file.blobName.split("/").slice(-2, -1)[0],
        fileName: file.fileName,
        path: `${file?.blobName}`,
        // width: file.width,
        // height: file.height,
      };
    });

    const docxData = buildCurrentReportDocxData({
      code: reportTemplateId as any,
      editedData: { [reportTemplateId]: editedDataCurrentReport } as any,
    }) as object;

    try {
      await doGenerateReportAppDocx(
        activeReport.template,
        docxData,
        images,
        project?.maconomy?.projectNo,
        activeReport.name,
      );
    } catch (error) {
      const message =
        typeof error?.message === "string"
          ? error.message
          : typeof error?.message?.message === "string"
          ? error.message.message
          : "Det er ingen docx-mal lastet opp";
      dispatch(setShowFailedMessageThunk(message));
    }
  };

  return (
    <>
      <ReportDocxDataModal opened={openJsonModal} onClose={() => setOpenJsonModal(false)} />
      {activeReport != null && (
        <Stack gap={0} style={{ background: "white", height: "100%", minHeight: 0 }}>
          {startupChecklist && (
            <StartupChecklistHeader
              templateId={activeReport.template}
              reportName={activeReport.name}
              reportData={editedDataCurrentReport}
              createdAt={activeReport.createdAt}
              updatedAt={activeReport.updatedAt}
              createdByName={activeReport.createdBy?.name}
              assignmentName={project?.maconomy?.name}
              mainAssignmentNumber={project?.maconomy?.projectNo}
              subAssignmentNumber={activeReport.doksam?.subTaskNo}
            />
          )}
          {isOnline === false && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              Du er offline, koble til internett for å lagre endringer.
            </Alert>
          )}
          {isSaving && (
            <Group>
              <MCLoader loading={isSaving} />
            </Group>
          )}
          <Prompt when={hasUnsavedChanges} message={onLocationChange} />
          {saveError && (
            <Alert color="red" title="Kunne ikke lagre">
              {saveError}
            </Alert>
          )}
          {reportErrors.ok === false && reportErrorsModalOpen === true && (
            <ZodErrorModal errors={reportErrors.error.issues} onAbort={() => setReportErrorsModalOpen(false)} />
          )}
          {startupChecklist && directSave.error && (
            <Alert color="red" title="Kunne ikke lagre">
              <Text size="sm">{directSave.error}</Text>
              {directSave.retry && (
                <Button mt="xs" size="xs" onClick={directSave.retry}>
                  Prøv igjen
                </Button>
              )}
            </Alert>
          )}
          {startupChecklist && reportIsValid === false && (
            <Alert color="orange" title="Kontroller feltene før lagring">
              <Button size="xs" variant="subtle" onClick={() => setReportErrorsModalOpen(true)}>
                Vis feil
              </Button>
            </Alert>
          )}
          {!startupChecklist && (
            <>
              <Group
                justify="space-between"
                align="center"
                wrap="nowrap"
                style={{
                  background: "white",
                  paddingBlock: 10,
                  paddingInline: 4,
                  borderBottom: "1px solid #e9ecef",
                  flexShrink: 0,
                }}
              >
                <Group gap="sm">
                  {!direct && (
                    <Button
                      variant="filled"
                      disabled={isSaving || isOnline === false || reportIsValid === false || isEditedDataEqual === true}
                      onClick={onSave}
                      color="green"
                      leftSection={<IconDeviceFloppy size={16} />}
                    >
                      Lagre
                    </Button>
                  )}
                  {direct && (
                    <Group gap="xs" aria-live="polite">
                      <Text size="sm" c={directSave.error ? "red" : "dimmed"}>
                        {directSave.error
                          ? directSave.error
                          : directSave.saving
                          ? "Lagrer…"
                          : isEditedDataEqual
                          ? "Lagret"
                          : !isOnline
                          ? "Ikke lagret – frakoblet"
                          : !reportIsValid
                          ? "Ikke lagret – kontroller feltene"
                          : "Venter på lagring…"}
                      </Text>
                      {directSave.error && directSave.retry && (
                        <Button size="xs" variant="subtle" onClick={directSave.retry}>
                          Prøv igjen
                        </Button>
                      )}
                    </Group>
                  )}

                  {reportIsValid === false && (
                    <Button
                      onClick={() => setReportErrorsModalOpen(true)}
                      color="orange"
                      variant="filled"
                      leftSection={<IconAlertTriangle size={16} />}
                    >
                      {reportErrors.error.issues?.length}
                    </Button>
                  )}
                </Group>
                <Group gap={8}>
                  <Tooltip label="Vis docx-data (JSON)" withArrow position="top">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="lg"
                      onClick={() => setOpenJsonModal(true)}
                      aria-label="Vis docx-data (JSON)"
                    >
                      <IconBraces size={18} />
                    </ActionIcon>
                  </Tooltip>

                  <Button
                    color="mc-primary"
                    variant="filled"
                    onClick={generateDocx}
                    disabled={reportIsValid === false}
                    leftSection={<IconFileTypeDocx size={18} />}
                  >
                    Generer dokument
                  </Button>
                </Group>
              </Group>
              <Divider mb="md" />
            </>
          )}

          {activeReport != null && (
            <div
              style={{
                overflow: startupChecklist ? "hidden" : "auto",
                display: startupChecklist ? "flex" : undefined,
                flex: 1,
                minHeight: 0,
              }}
            >
              {activeReport?.template === 102 && (
                <Brannrapport data={reportData as IBrandrapportConfig} onUpdateEditData={(data) => setData(data)} />
              )}
              {activeReport?.template === 103 && (
                <GeoteknikRapport
                  data={reportData as IGeoTeknikkRapport}
                  onUpdateEditData={(data) => setData(data)}
                  projectId={project?._id}
                  userName={user?.name}
                  createdAt={activeReport.createdAt as Date}
                />
              )}
              {activeReport?.template === 104 && (
                <>
                  <GKrapport
                    data={reportData as IGKRapport}
                    onUpdateEditData={(data) => setData(data)}
                    style={{ width: "50%" }}
                    projectId={activeReport.project}
                    createdAt={activeReport.createdAt as Date}
                  />
                </>
              )}
              {activeReport?.template === 106 && (
                <GeoteknikOmraadestabilitetsRapport
                  createdAt={activeReport.createdAt as string}
                  data={reportData as IGeoTeknikkRapport}
                  onUpdateEditData={(data) => setData(data)}
                  projectId={activeReport.project}
                  userName={user?.name}
                />
              )}
              {activeReport?.template === 201 && (
                <RapportSamsvarserklaringFEL
                  samsvarserklaring={reportData as IElektroSamsvarserklaringFELRapport}
                  onUpdateEditData={(data) => setData(data)}
                />
              )}
              {activeReport.template === 202 && (
                <div style={{ overflow: "auto" }}>
                  <RapportSamsvarserklaringEkomnett
                    data={reportData as IElektroSamsvarserklaringEkomnettRapport}
                    onUpdateEditData={(data) => setData(data)}
                  />
                </div>
              )}
              {activeReport?.template === 205 && (
                <SJARapport
                  createdAt={activeReport.createdAt as string}
                  data={reportData as ISJArapport}
                  onUpdateEditData={(data) => setData(data)}
                  projectId={activeReport.project}
                />
              )}
              {activeReport?.template === 301 && (
                <BygningsfysikkPremissrapport
                  createdAt={activeReport.createdAt as string}
                  data={reportData as IBygningsfysikkPremissRapport}
                  onUpdateEditData={(data) => setData(data)}
                />
              )}
              {activeReport?.template === 401 && <AkustikkPremissrapport />}
              {activeReport?.template === 701 && (
                <UnderkonsulentRapport
                  createdAt={activeReport.createdAt as string}
                  data={reportData as IInternJuridiskUnderkonsulentRapport}
                  onUpdateEditData={(data) => setData(data)}
                />
              )}
              {(activeReport?.template === 901 || activeReport?.template === 902) && (
                <OverleveringChecklistReport
                  data={reportData as IOverleveringChecklistReport | IForenkletStyringNivaa1Report}
                  directSave={directSave}
                  assessmentEnabled={activeReport.template === 902}
                />
              )}
              {activeReport?.template === 101 && <EksempelRapport />}
            </div>
          )}
        </Stack>
      )}
    </>
  );
};

export default SingleReport;
